import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { Reducer } from 'redux';

import { AddonMetadata, LocalizedInsuranceProduct, QuotePerProduct } from '@breathelife/types';

import { ApplicationState } from '../../Models/ApplicationState';

export type InsuranceApplicationState = {
  insuranceApplication?: ApplicationState;
  recommendedCoverage?: number;
  products: LocalizedInsuranceProduct[];
  quotes?: QuotePerProduct;
  isLoading: boolean;
  addonsMetadata?: AddonMetadata;
  referencePremium?: number;
};

const defaultState = {
  isLoading: false,
  products: [],
};

type InsuranceApplicationPayload = {
  insuranceApplication: ApplicationState;
};

type RecommendedCoveragePayload = number;
type QuotesPayload = QuotePerProduct;
type ProductsPayload = LocalizedInsuranceProduct[];
type AddonsMetadataPayload = AddonMetadata;
type ReferencePremiumPayload = number;

type InsuranceApplicationActions = {
  setInsuranceApplication: (payload: InsuranceApplicationPayload) => PayloadAction<InsuranceApplicationPayload>;
  setRecommendedCoverage: (payload: RecommendedCoveragePayload) => PayloadAction<RecommendedCoveragePayload>;
  setQuotes: (payload: QuotesPayload) => PayloadAction<QuotesPayload>;
  setProducts: (payload: ProductsPayload) => PayloadAction<ProductsPayload>;
  setIsLoading: (payload: boolean) => PayloadAction<boolean>;
  reset: () => PayloadAction<undefined>;
  resetQuotes: () => PayloadAction<undefined>;
  setAddons: (payload: AddonsMetadataPayload) => PayloadAction<AddonsMetadataPayload>;
  setReferencePremium: (payload: ReferencePremiumPayload) => PayloadAction<ReferencePremiumPayload>;
};

// `ApplicationState` is too deep a recursive type (via the form-engine-sdk blueprint types) for TS to
// compute `Draft<InsuranceApplicationState>` against (TS2589). Build the slice with the state generic
// left untyped, then cast `.reducer`/`.actions` back to the real shape below for consumers.
const untypedInsuranceApplicationSlice = createSlice({
  name: 'consumerFlow/insuranceApplication',
  initialState: defaultState as any,
  reducers: {
    setInsuranceApplication(state: InsuranceApplicationState, { payload }: PayloadAction<InsuranceApplicationPayload>) {
      state.insuranceApplication = payload.insuranceApplication;
    },
    setRecommendedCoverage(state: InsuranceApplicationState, { payload }: PayloadAction<RecommendedCoveragePayload>) {
      state.recommendedCoverage = payload;
    },
    setProducts(state: InsuranceApplicationState, { payload }: PayloadAction<ProductsPayload>) {
      if (!_.isEqual(state.products, payload)) state.products = payload;
    },
    setIsLoading(state: InsuranceApplicationState, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },
    setQuotes(state: InsuranceApplicationState, { payload }: PayloadAction<QuotesPayload>) {
      state.quotes = payload;
    },
    reset(state: InsuranceApplicationState) {
      state.insuranceApplication = undefined;
      state.recommendedCoverage = undefined;
      state.products = [];
      state.isLoading = false;
      state.quotes = undefined;
    },
    resetQuotes(state: InsuranceApplicationState) {
      state.recommendedCoverage = undefined;
      state.isLoading = false;
      state.quotes = undefined;
    },
    setAddons(state: InsuranceApplicationState, { payload }: PayloadAction<AddonsMetadataPayload>) {
      state.addonsMetadata = payload;
    },
    setReferencePremium(state: InsuranceApplicationState, { payload }: PayloadAction<ReferencePremiumPayload>) {
      state.referencePremium = payload;
    },
  },
});

export const insuranceApplicationSlice = {
  name: untypedInsuranceApplicationSlice.name,
  reducer: untypedInsuranceApplicationSlice.reducer as Reducer<InsuranceApplicationState>,
  actions: untypedInsuranceApplicationSlice.actions as unknown as InsuranceApplicationActions,
};
